/* eslint-disable jsx-a11y/no-autofocus */
import cn from 'classnames';
import { motion } from 'framer-motion';
import { Translate, t } from 'i18n';
import { ChangeEvent, FC, ReactNode, useCallback, useRef, useState } from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import crossIcon from 'students/views/shared/assets/icons/close_icon.svg';
import ButtonGeneral from 'students/views/shared/components/ButtonGeneral';
import PasteBlockedNotification, {
  usePasteBlockedNotification
} from 'students/views/shared/components/PasteBlockedNotification';
import UrlIcon from 'students/views/shared/components/UrlIcon';
import { useBreakPoint } from 'students/views/shared/hooks';
import { getPreference } from 'students/views/shared/providers/Preferences/helpers';
import { Preferences } from 'students/views/shared/providers/Preferences/models';
import { customMediaQuery } from 'students/views/shared/styled';
import styled from 'styled-components';

import DoneIcon from '../../../../assets/done_icon.svg';
import SendIcon from '../../../../assets/send_icon.svg';
import ShortEssayConfirmationModal from '../../../ShortEssayConfirmationModal';

interface IReplyTextarea {
  onSend: (answer: string) => void;
  minimumWordsCount: number;
  defaultValue?: string;
  isLoading: boolean;
  onClose?: () => void;
  className?: string;
  disabledSubmit?: boolean;
  forbidSameAnswer?: boolean;
  replyError?: ReactNode;
  withNbSupportCharacters?: boolean;
}

const ReplyTextarea: FC<IReplyTextarea> = ({
  onSend,
  minimumWordsCount,
  defaultValue,
  isLoading,
  disabledSubmit,
  forbidSameAnswer,
  onClose,
  replyError,
  withNbSupportCharacters,
  className
}) => {
  const isMobile = useBreakPoint('md', true);
  const [inputValue, setInputValue] = useState<string>(defaultValue || '');
  const [showModal, setShowModal] = useState(false);
  const isPasteAllowed = JSON.parse(getPreference(Preferences.EssayAIPaste, 'false'));
  const { isNoticeVisible, handlePaste, dismissNotice } =
    usePasteBlockedNotification(isPasteAllowed);
  const textareaRef = useRef<HTMLTextAreaElement | null>();

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      setInputValue(event.target.value);
    },
    [setInputValue]
  );

  const handleSend = () => {
    if (currentWordsCount < minimumWordsCount) {
      setShowModal(true);
    } else {
      onSend(inputValue);
    }
  };

  const currentWordsCount = inputValue.match(/[^\s]+/g)?.length || 0;
  const isSameAnswer = inputValue.trim() === defaultValue?.trim();
  return (
    <>
      <SWrapper
        layout
        className={cn(className)}
        initial={{ opacity: 0, scaleY: 0 }}
        animate={{ opacity: 1, scaleY: 1 }}
        dir="ltr"
      >
        {replyError}

        <STextareaWrapper>
          <TextareaAutosize
            // @ts-expect-error
            ref={textareaRef}
            autoFocus
            rows={isMobile ? 1 : 5}
            maxRows={isMobile ? 12 : undefined}
            value={inputValue}
            onChange={handleChange}
            onPaste={handlePaste}
            disabled={isLoading}
            placeholder={`${t(
              'frontend.lesson_task.tasks.check.assignment.type_your_answer'
            )}...`}
          />
        </STextareaWrapper>

        <SFooter>
          <SFlex>
            {withNbSupportCharacters ? (
              <SCharactersBlock role="group" aria-label="Special characters">
                {['æ', 'ø', 'å'].map((character: string) => (
                  <SCharacterButton
                    key={character}
                    type="button"
                    aria-label={`Insert ${character}`}
                    onClick={() => {
                      setInputValue(inputValue + character);
                      textareaRef.current?.focus();
                    }}
                  >
                    {character}
                  </SCharacterButton>
                ))}
              </SCharactersBlock>
            ) : null}

            <SRequirementText>
              <Translate
                str="frontend.lesson_task.tasks.check.assignment.minimum_words_amount"
                params={{
                  minimumAmount: minimumWordsCount
                }}
              />
            </SRequirementText>
          </SFlex>

          <SRequirementBlock>
            <SSendButton
              type="button"
              disabled={
                disabledSubmit ||
                !currentWordsCount ||
                isLoading ||
                (!!forbidSameAnswer && isSameAnswer)
              }
              onClick={handleSend}
            >
              {t('frontend.lesson_task.tasks.check.assignment.send', {
                defaultValue: 'Send'
              })}
              <UrlIcon url={SendIcon} height="20px" width="20px" color="currentColor" />
            </SSendButton>

            <SRequiredSymbols>
              {currentWordsCount >= minimumWordsCount && (
                <SCheckIcon>
                  <UrlIcon url={DoneIcon} color="#ffffff" height="9px" width="9px" />
                </SCheckIcon>
              )}

              <SRequiredSymbolsText
                className={cn({
                  notEmpty: currentWordsCount > 0,
                  correct: currentWordsCount >= minimumWordsCount
                })}
              >
                {currentWordsCount}/{minimumWordsCount}
              </SRequiredSymbolsText>
            </SRequiredSymbols>
          </SRequirementBlock>
        </SFooter>

        {typeof onClose === 'function' ? (
          <SCloseBtn onClick={onClose}>
            <UrlIcon
              url={crossIcon}
              height="16px"
              width="16px"
              color="var(--heading-text-color)"
            />
          </SCloseBtn>
        ) : null}
      </SWrapper>

      <ShortEssayConfirmationModal
        onSend={() => onSend(inputValue)}
        show={showModal}
        close={() => setShowModal(false)}
        message={
          <Translate
            str="frontend.lesson_task.tasks.check.assignment.essay_does_not_have_enough_words"
            params={{
              minWordsNumber: minimumWordsCount,
              currentWordsNumber: currentWordsCount
            }}
          />
        }
        continueBtnText={
          <Translate str="frontend.lesson_task.tasks.check.assignment.continue_writing" />
        }
      />

      <PasteBlockedNotification show={isNoticeVisible} onClose={dismissNotice} />
    </>
  );
};

export default ReplyTextarea;

const SWrapper = styled(motion.div)`
  transform-origin: bottom;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  width: 100%;
  position: relative;
  background-color: var(--assignments-feedback-message-background);
  padding: 16px;

  ${customMediaQuery('tablet')} {
    background-color: var(--assignments-reply-input-background);
    border-radius: 14px;
    padding: 24px 32px 80px 24px;
  }
`;

const STextareaWrapper = styled.div`
  width: 100%;
  z-index: 1;

  ${customMediaQuery('tablet')} {
    overflow-y: auto;
    max-height: 40vh;
    scrollbar-width: thin;

    &::-webkit-scrollbar {
      display: block;
      width: 2px;
    }

    &::-webkit-scrollbar-thumb {
      background: var(--sub-text-color);
      border-radius: 4px;
    }
  }

  & > textarea {
    width: 100%;
    background: transparent;
    border: none;
    font-size: 1rem;
    font-weight: 400;
    line-height: 1.5rem;
    color: var(--heading-text-color);
    resize: none;
    min-height: 44px;
    overflow-y: auto;

    &:focus,
    &:active {
      outline: none;
    }

    &::-webkit-input-placeholder {
      color: var(--sub-text-color);
    }

    &::placeholder {
      color: var(--sub-text-color);
    }

    ${customMediaQuery('tablet')} {
      font-size: 1rem;
      line-height: 1.5rem;
      overflow-y: visible;
      min-height: 0;
    }
  }
`;

const SFlex = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
`;

const SCharactersBlock = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
`;

const SCharacterButton = styled.button`
  background: rgba(11, 20, 32, 0.1);
  border: none;
  border-radius: 8px;
  color: var(--lingu-font-dark-color);
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  padding: 6px;
  transition: backgroung 0.3s, color 0.3s;
  width: 36px;

  &:hover,
  &:focus {
    outline: none;
    background: rgba(11, 20, 32, 0.2);
  }

  &:active {
    background: rgba(11, 20, 32, 0.35);
  }
`;

const SFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  width: 100%;
  padding: 8px 0 0;

  ${customMediaQuery('tablet')} {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    width: auto;
    padding: 16px;
  }
`;

const SRequirementText = styled.p`
  margin: 0;
  padding: 0;
  font-size: 0.75rem;
  line-height: 1rem;
  font-weight: 500;
  color: var(--color-black);

  ${customMediaQuery('tablet')} {
    font-size: 0.875rem;
    line-height: 1.25rem;
  }
`;

const SRequirementBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;

  ${customMediaQuery('tablet')} {
    flex-direction: row-reverse;

    & > div {
      margin-inline-end: 20px;
    }
  }
`;

const SRequiredSymbols = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;

  & > div:first-child {
    margin-inline-end: 4px;
  }
`;

const SCheckIcon = styled.div`
  background: var(--color-green);
  border-radius: 50%;
  padding: 3px;

  ${customMediaQuery('tablet')} {
    height: 15px;
    width: 15px;
  }
`;

const SRequiredSymbolsText = styled.p`
  margin: 0;
  padding: 0;
  color: var(--color-black);
  font-weight: 600;
  font-size: 0.75rem;
  line-height: 1rem;

  &.correct {
    color: var(--color-green);
  }

  ${customMediaQuery('tablet')} {
    font-size: 0.875rem;
    line-height: 1.25rem;
  }
`;

const SSendButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--color-blue-700);
  color: var(--color-white);
  border: none;
  margin: 0 0 14px;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s, opacity 0.2s;

  &:hover:not(:disabled) {
    background: var(--color-blue-800);
  }

  &:focus {
    outline: 2px solid var(--color-blue-800);
    outline-offset: 2px;
  }

  &:disabled {
    background: var(--color-blue-btn-bg-disabled);
    color: var(--color-blue-btn-color-disabled);
    opacity: 0.6;
    cursor: not-allowed;
  }

  ${customMediaQuery('tablet')} {
    margin: 0;
    padding: 10px 20px;
    font-size: 1rem;
  }
`;

const SCloseBtn = styled(ButtonGeneral)`
  border: none;
  background: var(--card-background-color);
  border-radius: 50%;
  position: absolute;
  top: -36px;
  padding: 8px;
  right: 8px;
  opacity: 0.7;
  transition: opacity 0.3s;

  &:hover {
    opacity: 0.7;
  }

  ${customMediaQuery('tablet')} {
    background: transparent;
    top: 10px;
    right: 6px;
  }
`;
